import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import useSeo from '@/hooks/useSeo';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/lore';

export default function LoreDetail() {
  const { slug = '' } = useParams();
  const article = getArticleBySlug(slug);

  useSeo({
    title: article ? `${article.title} - 洞洞杂货铺漫画` : '文章未找到 - 洞洞杂货铺漫画',
    description: article ? article.excerpt : '洞洞杂货铺漫画设定文章未找到。',
    keywords: '洞洞杂货铺漫画,洞洞杂货铺设定,洞洞杂货铺世界观,洞洞杂货铺故事背景',
    ogType: 'article',
    jsonLd: article
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: { '@type': 'Organization', name: '洞洞杂货铺漫画内容站' },
          publisher: { '@type': 'Organization', name: '洞洞杂货铺漫画内容站' },
        }
      : undefined,
  });

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-6xl flex-col items-center px-4 py-32 text-center md:px-6">
          <h1 className="text-2xl text-foreground-950">这篇文章似乎走丢了</h1>
          <p className="mt-3 text-foreground-600">你寻找的设定内容暂时不存在，返回列表看看其他文章吧。</p>
          <Link
            to="/lore"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回设定列表
          </Link>
        </div>
      </Layout>
    );
  }

  const related = getRelatedArticles(article.slug);

  return (
    <Layout>
      <article className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-16">
        <nav aria-label="面包屑" className="text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
          <span className="mx-2 text-foreground-300">/</span>
          <Link to="/lore" className="transition-colors hover:text-primary-700">设定与世界观</Link>
          <span className="mx-2 text-foreground-300">/</span>
          <span className="text-foreground-700">正文</span>
        </nav>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-accent-100 px-3 py-0.5 text-xs text-accent-800">{article.category}</span>
          {article.tag && (
            <span className="rounded-full bg-secondary-100 px-3 py-0.5 text-xs text-secondary-900">{article.tag}</span>
          )}
        </div>
        <h1 className="mt-4 text-2xl leading-relaxed text-foreground-950 md:text-4xl md:leading-snug">{article.title}</h1>
        <p className="mt-4 text-base leading-relaxed text-foreground-600">{article.excerpt}</p>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-sm text-foreground-500">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-calendar-line" aria-hidden="true"></i>
            {article.date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true"></i>
            阅读约 {article.readTime}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-eye-line" aria-hidden="true"></i>
            {article.views} 阅读
          </span>
        </div>

        <div className="mt-8 overflow-hidden rounded-lg">
          <img src={article.cover} alt={article.coverAlt} className="h-full w-full object-cover object-top" />
        </div>

        <div className="prose-content mt-9">
          <p className="text-lg leading-relaxed text-foreground-700">{article.content.intro}</p>
          {article.content.sections.map((section) => (
            <div key={section.heading}>
              <h2>{section.heading}</h2>
              {section.body.map((para) => (
                <p key={para}>{para}</p>
              ))}
              {section.sub?.map((sub) => (
                <div key={sub.heading}>
                  <h3>{sub.heading}</h3>
                  {sub.body.map((para) => (
                    <p key={para}>{para}</p>
                  ))}
                </div>
              ))}
            </div>
          ))}
          <blockquote>{article.content.conclusion}</blockquote>
        </div>
      </article>

      <section className="border-t border-background-200 bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="flex items-end justify-between">
            <h2 className="text-xl text-foreground-950 md:text-2xl">继续阅读</h2>
            <Link to="/lore" className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary-700 transition-colors hover:text-primary-800">
              返回全部设定
              <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="mt-7 grid gap-5 md:grid-cols-3">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/lore/${a.slug}`}
                className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200"
              >
                <span className="text-xs text-foreground-500">{a.category}</span>
                <h3 className="mt-2 text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {a.title}
                </h3>
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary-700">
                  阅读全文
                  <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}